import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Spinner, Badge, Button, Alert, Form } from "react-bootstrap";
import api, { BASE_URL } from "../../services/api";
import { getStoredUser } from "../../utils/authStorage";
import "./Dashboard.css";

const jenisPelatihanLabel = {
  PPH01: "Pelatihan Penyelia Halal UMK Self Declare",
  PPH02: "Pelatihan Penyelia Halal UMK Reguler",
  PPH03: "Pelatihan Penyelia Halal Reguler",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("id-ID", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getStatusVariant = (status) => {
  switch ((status || "").toUpperCase()) {
    case "PENDAFTARAN": return "warning";
    case "LULUS": return "success";
    case "DITOLAK": return "danger";
    case "PROSES": return "info";
    default: return "secondary";
  }
};

const ProfilPenyelia = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Resubmit state
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [reFiles, setReFiles] = useState({ file_ktp: null, file_foto: null, bukti_pembayaran: null });
  const ktpRef = useRef();
  const fotoRef = useRef();
  const buktiRef = useRef();

  // Sertifikat TTE URL from SIHALAL
  const [tteUrl, setTteUrl]     = useState(null);
  const [tteLoding, setTteLoading] = useState(false);

  useEffect(() => {
    const parsedUser = getStoredUser();
    if (parsedUser) {
      const email = parsedUser.userid || parsedUser.email_addr;
      if (email) {
        fetchProfile(email);
      } else {
        setError("Data pengguna tidak ditemukan");
        setLoading(false);
      }
    } else {
      setError("Sesi tidak ditemukan, silakan login ulang");
      setLoading(false);
    }
  }, []);

  const fetchProfile = async (email) => {
    try {
      setLoading(true);
      const response = await api.get(`/pendaftaran-penyelia/profil/${encodeURIComponent(email)}`);
      if (response.data.success) {
        const prof = response.data.data;
        setProfile(prof);
        // Fetch TTE URL jika sudah LULUS
        if ((prof.status || "").toUpperCase() === "LULUS") {
          fetchTteUrl(email);
        }
      } else {
        setError(response.data.message || "Data profil tidak ditemukan");
      }
    } catch (err) {
      console.error("Error fetching profil penyelia:", err);
      setError(err.response?.data?.message || "Gagal memuat data profil");
    } finally {
      setLoading(false);
    }
  };

  const fetchTteUrl = async (email) => {
    try {
      setTteLoading(true);
      const res = await api.get(`/pendaftaran-penyelia/sertifikat-tte/${encodeURIComponent(email)}`);
      if (res.data.success && res.data.url) setTteUrl(res.data.url);
    } catch (err) {
      console.error("Error fetching TTE URL:", err);
    } finally {
      setTteLoading(false);
    }
  };

  const buildFileUrl = (filename) => {
    if (!filename) return null;
    return `${BASE_URL}/uploads/penyelia/${filename}`;
  };

  const buildSertifikatUrl = (filename) => {
    if (!filename) return null;
    return `${BASE_URL}/uploads/sertifikat/${filename}`;
  };

  const getUserEmail = () => {
    const p = getStoredUser();
    if (!p) return null;
    return p.userid || p.email_addr || null;
  };

  const handleReFileChange = (field, file) => {
    setReFiles((prev) => ({ ...prev, [field]: file || null }));
  };

  const handleResubmit = async () => {
    const email = getUserEmail();
    if (!email) return;
    try {
      setSubmitLoading(true);
      setSubmitError("");
      setSubmitSuccess("");
      const fd = new FormData();
      fd.append("email", email);
      if (reFiles.file_ktp) fd.append("file_ktp", reFiles.file_ktp);
      if (reFiles.file_foto) fd.append("file_foto", reFiles.file_foto);
      if (reFiles.bukti_pembayaran) fd.append("bukti_pembayaran", reFiles.bukti_pembayaran);
      await api.put("/pendaftaran-penyelia/resubmit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitSuccess("Pendaftaran berhasil disubmit ulang. Status Anda kembali ke PENDAFTARAN.");
      setReFiles({ file_ktp: null, file_foto: null, bukti_pembayaran: null });
      if (ktpRef.current) ktpRef.current.value = "";
      if (fotoRef.current) fotoRef.current.value = "";
      if (buktiRef.current) buktiRef.current.value = "";
      // Refresh profile
      fetchProfile(email);
    } catch (err) {
      setSubmitError(err.response?.data?.message || "Gagal submit ulang, silakan coba lagi.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const canResubmit = (status) => {
    const s = (status || "").toUpperCase();
    return s !== "LULUS" && s !== "TIDAK LULUS";
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Memuat data profil...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-4">{error}</div>
    );
  }

  if (!profile) return null;

  return (
    <div className="dashboard-content">
      <div className="dashboard-header mb-4">
        <h2>
          <i className="fas fa-user-circle me-2"></i>
          Profil Penyelia Halal
        </h2>
        <p className="text-muted mb-0">Data pendaftaran Anda sebagai Calon Penyelia Halal</p>
      </div>

      {/* Keterangan / Alasan Penolakan */}
      {profile.keterangan && (
        <div className="alert alert-danger d-flex align-items-start gap-3 mb-4 shadow-sm" role="alert">
          <i className="fas fa-exclamation-circle fa-lg mt-1 flex-shrink-0"></i>
          <div>
            <div className="fw-bold mb-1">Keterangan dari Admin</div>
            <div>{profile.keterangan}</div>
          </div>
        </div>
      )}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                <i className="fas fa-id-badge fa-2x text-primary"></i>
              </div>
              <div>
                <div className="text-muted small">No. Register</div>
                <div className="fw-bold fs-5">{profile.no_register || "-"}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="rounded-circle bg-success bg-opacity-10 p-3">
                <i className="fas fa-info-circle fa-2x text-success"></i>
              </div>
              <div>
                <div className="text-muted small">Status Pendaftaran</div>
                <Badge bg={getStatusVariant(profile.status)} className="fs-6 px-3 py-2">
                  {profile.status || "PENDAFTARAN"}
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Data Pribadi */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <i className="fas fa-user me-2"></i>Data Pribadi
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <table className="table table-borderless table-sm">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: "40%" }}>Nama Lengkap</td>
                    <td className="fw-semibold">{profile.nama || "-"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Email</td>
                    <td>{profile.email || "-"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">No. HP</td>
                    <td>{profile.no_hp || "-"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">NIK</td>
                    <td>{profile.nik || "-"}</td>
                  </tr>
                </tbody>
              </table>
            </Col>
            <Col md={6}>
              <table className="table table-borderless table-sm">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: "40%" }}>Tempat Lahir</td>
                    <td>{profile.tempat_lahir || "-"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Tanggal Lahir</td>
                    <td>{formatDate(profile.tgl_lahir)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Nama Instansi</td>
                    <td>{profile.nama_instansi || "-"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Tanggal Daftar</td>
                    <td>{formatDate(profile.tgl_daftar)}</td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Alamat */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-info text-white">
          <i className="fas fa-map-marker-alt me-2"></i>Alamat
        </Card.Header>
        <Card.Body>
          <table className="table table-borderless table-sm">
            <tbody>
              <tr>
                <td className="text-muted" style={{ width: "20%" }}>Alamat Lengkap</td>
                <td>{profile.alamat_lengkap || "-"}</td>
              </tr>
              <tr>
                <td className="text-muted">Provinsi</td>
                <td>{profile.provinsi || "-"}</td>
              </tr>
              <tr>
                <td className="text-muted">Kabupaten/Kota</td>
                <td>{profile.kabupaten || "-"}</td>
              </tr>
              <tr>
                <td className="text-muted">Kecamatan</td>
                <td>{profile.kecamatan || "-"}</td>
              </tr>
              <tr>
                <td className="text-muted">Kode Pos</td>
                <td>{profile.kode_pos || "-"}</td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      {/* Jenis Pelatihan */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-warning text-dark">
          <i className="fas fa-chalkboard-teacher me-2"></i>Jenis Pelatihan
        </Card.Header>
        <Card.Body>
          <table className="table table-borderless table-sm">
            <tbody>
              <tr>
                <td className="text-muted" style={{ width: "20%" }}>Jenis Pelatihan</td>
                <td>{jenisPelatihanLabel[profile.jenis_pelatihan] || profile.jenis_pelatihan || "-"}</td>
              </tr>
              <tr>
                <td className="text-muted">Angkatan</td>
                <td>{profile.angkatan || "-"}</td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      {/* Data SPPG (jika ada) */}
      {profile.is_penyelia_halal_sppg ? (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-secondary text-white">
            <i className="fas fa-building me-2"></i>Data SPPG
          </Card.Header>
          <Card.Body>
            <table className="table table-borderless table-sm">
              <tbody>
                <tr>
                  <td className="text-muted" style={{ width: "20%" }}>Nama SPPG</td>
                  <td>{profile.nama_sppg || "-"}</td>
                </tr>
                <tr>
                  <td className="text-muted">Alamat SPPG</td>
                  <td>{profile.alamat_sppg || "-"}</td>
                </tr>
                <tr>
                  <td className="text-muted">Provinsi SPPG</td>
                  <td>{profile.provinsi_sppg || "-"}</td>
                </tr>
                <tr>
                  <td className="text-muted">Kabupaten SPPG</td>
                  <td>{profile.kabupaten_sppg || "-"}</td>
                </tr>
                <tr>
                  <td className="text-muted">Kecamatan SPPG</td>
                  <td>{profile.kecamatan_sppg || "-"}</td>
                </tr>
                <tr>
                  <td className="text-muted">Kelurahan SPPG</td>
                  <td>{profile.kelurahan_sppg || "-"}</td>
                </tr>
              </tbody>
            </table>
          </Card.Body>
        </Card>
      ) : null}

      {/* Dokumen */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-dark text-white">
          <i className="fas fa-file-alt me-2"></i>Dokumen
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <div className="text-muted small mb-1">KTP</div>
              {profile.file_ktp ? (
                <a
                  href={buildFileUrl(profile.file_ktp)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="fas fa-eye me-1"></i> Lihat KTP
                </a>
              ) : (
                <span className="text-muted">-</span>
              )}
            </Col>
            <Col md={4}>
              <div className="text-muted small mb-1">Foto</div>
              {profile.file_foto ? (
                <a
                  href={buildFileUrl(profile.file_foto)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="fas fa-eye me-1"></i> Lihat Foto
                </a>
              ) : (
                <span className="text-muted">-</span>
              )}
            </Col>
            <Col md={4}>
              <div className="text-muted small mb-1">Bukti Pembayaran</div>
              {profile.bukti_pembayaran ? (
                <a
                  href={buildFileUrl(profile.bukti_pembayaran)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="fas fa-eye me-1"></i> Lihat Bukti
                </a>
              ) : (
                <span className="text-muted">-</span>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Submit Ulang */}
      {canResubmit(profile.status) && (
        <Card className="mb-4 border-0 shadow-sm border-warning">
          <Card.Header className="bg-warning text-dark">
            <i className="fas fa-redo me-2"></i>Submit Ulang Dokumen
          </Card.Header>
          <Card.Body>
            {submitSuccess && <Alert variant="success">{submitSuccess}</Alert>}
            {submitError && <Alert variant="danger">{submitError}</Alert>}
            <p className="text-muted small mb-3">
              Upload file baru untuk mengganti dokumen yang sudah ada. File yang tidak diganti akan tetap menggunakan file lama.
              Setelah submit, status pendaftaran Anda akan kembali menjadi <Badge bg="warning" text="dark">PENDAFTARAN</Badge>.
            </p>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label className="fw-semibold">KTP (opsional)</Form.Label>
                {profile.file_ktp && (
                  <div className="mb-1">
                    <a href={buildFileUrl(profile.file_ktp)} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                      <i className="fas fa-eye me-1"></i>Lihat File Saat Ini
                    </a>
                  </div>
                )}
                <Form.Control
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  ref={ktpRef}
                  onChange={(e) => handleReFileChange("file_ktp", e.target.files[0])}
                />
              </Col>
              <Col md={4}>
                <Form.Label className="fw-semibold">Foto (opsional)</Form.Label>
                {profile.file_foto && (
                  <div className="mb-1">
                    <a href={buildFileUrl(profile.file_foto)} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                      <i className="fas fa-eye me-1"></i>Lihat File Saat Ini
                    </a>
                  </div>
                )}
                <Form.Control
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  ref={fotoRef}
                  onChange={(e) => handleReFileChange("file_foto", e.target.files[0])}
                />
              </Col>
              <Col md={4}>
                <Form.Label className="fw-semibold">Bukti Pembayaran (opsional)</Form.Label>
                {profile.bukti_pembayaran && (
                  <div className="mb-1">
                    <a href={buildFileUrl(profile.bukti_pembayaran)} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                      <i className="fas fa-eye me-1"></i>Lihat File Saat Ini
                    </a>
                  </div>
                )}
                <Form.Control
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  ref={buktiRef}
                  onChange={(e) => handleReFileChange("bukti_pembayaran", e.target.files[0])}
                />
              </Col>
            </Row>
            <div className="mt-3">
              <Button variant="warning" onClick={handleResubmit} disabled={submitLoading}>
                {submitLoading
                  ? <><Spinner animation="border" size="sm" className="me-1" />Mengirim...</>
                  : <><i className="fas fa-paper-plane me-1"></i>Submit Ulang Pendaftaran</>}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Sertifikat */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-success text-white">
          <i className="fas fa-certificate me-2"></i>Sertifikat
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <div className="fw-semibold mb-1">Sertifikat</div>
              {profile.file_sertifikat ? (
                <a
                  href={buildSertifikatUrl(profile.file_sertifikat)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-success btn-sm"
                  download
                >
                  <i className="fas fa-download me-1"></i> Download Sertifikat
                </a>
              ) : (
                <span className="text-muted fst-italic">File Belum Ada</span>
              )}
            </Col>
            <Col md={6}>
              <div className="fw-semibold mb-1">Sertifikat TTE (Tanda Tangan Elektronik)</div>
              {tteLoding ? (
                <span className="text-muted fst-italic"><Spinner animation="border" size="sm" className="me-1" />Memeriksa...</span>
              ) : tteUrl ? (
                <a
                  href={tteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-success btn-sm"
                >
                  <i className="fas fa-download me-1"></i> Download Sertifikat TTE
                </a>
              ) : (
                <span className="text-muted fst-italic">File Belum Ada</span>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ProfilPenyelia;
